import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroWalkthroughVideo from "@/components/HeroWalkthroughVideo";
import HomeContactForm from "@/components/HomeContactForm";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, ArrowUpRight, Lock } from "lucide-react";
import { STATES, STATE_SLUGS } from "@/data/stateData";
import { POSTS } from "@/data/blogData";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1622396481322-3b83d186701b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const LEDGER_IMAGE =
  "https://images.unsplash.com/photo-1621866267790-bb82cab8f760?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/content/landing").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const pillars = data?.counterclaim_pillars ?? [];
  const steps = data?.process_steps ?? [];
  const testimonials = data?.testimonials ?? [];
  const faq = data?.faq ?? [];

  return (
    <div className="min-h-screen bg-paper text-stone-900" data-testid="landing-page">
      <link rel="canonical" href="https://azuregypsyguild.org/" />
      <Navbar />

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-end">
            {/* Left — editorial headline */}
            <div className="lg:col-span-7 fade-up">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-px bg-stone-900" />
                <span className="overline" data-testid="hero-eyebrow">
                  MMXXVI · Vol. I · Trustee Ledger
                </span>
              </div>

              <h1
                className="font-serif font-light text-[10vw] md:text-[7vw] lg:text-[6.5vw] leading-[0.92] tracking-tighter text-stone-900 text-balance"
                data-testid="hero-headline"
              >
                Counterclaim<br />
                relief in <span className="font-editorial italic text-stone-500">sixty</span> days.
              </h1>

              <p
                className="mt-10 text-lg md:text-xl text-stone-600 font-light leading-relaxed max-w-2xl text-pretty"
                data-testid="hero-sub"
              >
                Azure Gypsy Guild files a verified counterclaim against the law firm and bank,
                pledges funds into the trustee account, and reassigns the mortgage — settling
                foreclosure through a good accounting ledger audit.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-6">
                <Link to="/register" className="btn-primary" data-testid="hero-cta-join">
                  Join the Guild <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#process" className="btn-ghost" data-testid="hero-cta-process">
                  See the 60-day process
                </a>
              </div>
            </div>

            {/* Right — architectural image, harsh crop */}
            <div className="lg:col-span-5 fade-up fade-up-delay-1">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="Modern architecture"
                  className="w-full h-full object-cover grayscale contrast-[1.05]"
                />
                <div className="absolute top-4 left-4 caption text-white bg-stone-900/80 px-3 py-1.5">
                  Fig. 01 · The Estate
                </div>
              </div>
              <div className="mt-6 caption text-stone-500">
                Photographed for The Trustee Ledger — Vol. I
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ STATS BAR ═══════════ */}
      <section className="border-b border-stone-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3">
          {[
            { k: "01", value: `${data?.settlement_rate ?? 98}%`, label: "Settlement Rate", desc: "on completed cases" },
            { k: "02", value: "60", label: "Days to Relief", desc: "median settlement" },
            { k: "03", value: `$${(data?.membership_fee ?? 1500).toLocaleString()}`, label: "Yearly Membership", desc: "auto-renewing" },
          ].map((s, i) => (
            <div
              key={s.k}
              className={`py-14 md:py-20 px-2 md:px-8 ${i > 0 ? "border-t md:border-t-0 md:border-l border-stone-200" : ""}`}
              data-testid={`hero-stat-${s.k}`}
            >
              <div className="caption text-stone-400 mb-6">{s.k}</div>
              <div className="font-serif font-light text-6xl md:text-7xl leading-none text-stone-900 tracking-tighter">
                {s.value}
              </div>
              <div className="mt-6 flex items-baseline justify-between">
                <span className="overline">{s.label}</span>
                <span className="text-xs text-stone-500 font-editorial italic">{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ FOUR PILLARS · LEDGER LAYOUT ═══════════ */}
      <section id="counterclaims" className="py-24 md:py-40 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-5">
              <div className="overline mb-6">§ Counterclaims</div>
              <h2
                className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance"
                data-testid="counterclaims-heading"
              >
                Four pillars carry every Guild case.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-stone-600 font-light leading-relaxed text-pretty">
                Every case rests on the same architecture: signed agreements, ledger audits, and
                trustee oversight. Nothing is improvised. Nothing is left to chance.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-stone-200">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                className={`py-10 md:py-14 md:px-8 first:md:pl-0 last:md:pr-0 ${
                  i > 0 ? "border-t md:border-t-0 md:border-l border-stone-200" : ""
                }`}
                data-testid={`pillar-${i}`}
              >
                <div className="caption text-stone-400 mb-8">
                  0{i + 1} / 0{pillars.length}
                </div>
                <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight text-stone-900">
                  {p.title}
                </h3>
                <p className="text-stone-600 mt-5 text-[15px] leading-relaxed font-light">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 60-DAY PROCESS · OVERSIZED NUMERALS ═══════════ */}
      <section id="process" className="py-24 md:py-40 bg-stone-50 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-6">
              <div className="overline mb-6">§ The 60-Day Process</div>
              <h2
                className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance"
                data-testid="process-heading"
              >
                Seven trustee-led steps to settlement.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-base text-stone-600 font-light leading-relaxed">
                Every case runs the same sequence, in the same order, on the same clock. A member
                sees each step happen — signed, filed, audited.
              </p>
            </div>
          </div>

          <ol className="space-y-0" data-testid="process-steps">
            {steps.map((s, idx) => (
              <li
                key={s.key}
                className="group grid grid-cols-12 gap-6 md:gap-10 items-start py-10 md:py-14 border-t border-stone-200 last:border-b hover:bg-white transition-colors"
                data-testid={`process-step-${idx}`}
              >
                <div className="col-span-2 md:col-span-2">
                  <div className="numeral-xl text-6xl md:text-8xl group-hover:text-stone-900 transition-colors">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="col-span-10 md:col-span-7">
                  <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight text-stone-900">
                    {s.label}
                  </h3>
                  <p className="text-stone-600 mt-4 text-[15px] md:text-base leading-relaxed max-w-2xl">
                    {s.desc}
                  </p>
                </div>
                <div className="hidden md:block col-span-3 pt-3 text-right">
                  <span className="caption text-stone-500">{s.days}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ═══════════ MEMBER PORTAL PREVIEW ═══════════ */}
      <section className="py-24 md:py-40 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline mb-6">§ Member Portal</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-balance">
              Every filing, timestamped and visible.
            </h2>
            <p className="mt-8 text-lg text-stone-600 font-light leading-relaxed">
              A private members console shows each signed agreement, each uploaded exhibit, each
              trustee reply, and the running ledger balance — in real time.
            </p>
            <Link to="/register" className="btn-primary mt-10 inline-flex" data-testid="portal-cta">
              Open your ledger <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 border border-stone-200 bg-white">
            <HeroWalkthroughVideo />
          </div>
        </div>
      </section>

      {/* ═══════════ TESTIMONIES ═══════════ */}
      <section id="testimonies" className="py-24 md:py-40 bg-stone-50 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-6">
              <div className="overline mb-6">§ Testimonies</div>
              <h2
                className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance"
                data-testid="testimonies-heading"
              >
                Rescued estates,<br />in their own words.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-stone-200" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <blockquote
                key={`${t.name}-${i}`}
                className={`p-8 md:p-12 border-b border-stone-200 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } bg-white hover:bg-stone-100/60 transition-colors`}
                data-testid={`testimonial-${i}`}
              >
                <p className="font-serif font-light italic text-2xl md:text-3xl leading-[1.35] text-stone-900 text-balance">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <footer className="mt-8 flex items-center gap-4 pt-6 border-t border-stone-200">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="h-12 w-12 grayscale object-cover"
                  />
                  <div>
                    <div className="font-serif text-base text-stone-900">{t.name}</div>
                    <div className="caption text-stone-500 mt-1">{t.location}</div>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ FAQ ═══════════ */}
      <section id="faq" className="py-24 md:py-40 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-6">§ Frequently Asked</div>
            <h2
              className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-balance sticky top-32"
              data-testid="faq-heading"
            >
              Everything, answered plainly.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {faq.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-stone-200 border-t-0"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-serif text-xl md:text-2xl font-light py-7 hover:text-stone-500 hover:no-underline">
                    <span className="flex items-baseline gap-6">
                      <span className="caption text-stone-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span>{f.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 text-base leading-relaxed pb-7 pl-14 pr-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ═══════════ RELATED SEO RESOURCES · internal linking ═══════════ */}
      <section className="py-24 md:py-32 bg-stone-50 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-6">
              <div className="overline mb-6">§ Guides &amp; Resources</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-balance">
                Read the case-file<br />before you apply.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-base text-stone-600 font-light leading-relaxed">
                Three long-form guides explaining what the Guild does — the sixty-day timeline,
                the no-bankruptcy path, and the emergency sheriff-sale desk.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 border-t border-l border-stone-200 bg-white">
            {[
              { to: "/stop-foreclosure-fast", eyebrow: "01 · Timeline", h: "Stop foreclosure fast", sub: "The full 60-day timeline, day by day." },
              { to: "/mortgage-relief-without-bankruptcy", eyebrow: "02 · Alternative", h: "Relief without bankruptcy", sub: "How the counterclaim skips Chapter 7 and 13 entirely." },
              { to: "/sheriff-sale-help", eyebrow: "03 · Emergency", h: "Sheriff-sale help", sub: "What actually stops a scheduled sheriff sale." },
            ].map((c, i) => (
              <Link
                key={c.to}
                to={c.to}
                className={`p-8 md:p-10 border-r border-b border-stone-200 hover:bg-stone-900 hover:text-stone-50 transition-colors group ${i > 0 ? "md:border-l-0" : ""}`}
                data-testid={`landing-seo-tile-${i}`}
              >
                <div className="caption text-stone-400 group-hover:text-stone-500 mb-6">{c.eyebrow}</div>
                <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight">{c.h}</h3>
                <p className="mt-4 text-sm text-stone-600 group-hover:text-stone-300 font-light leading-relaxed">
                  {c.sub}
                </p>
                <ArrowUpRight className="h-5 w-5 mt-8 text-stone-400 group-hover:text-stone-50 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BY STATE · site-wide state-page linking for SEO ═══════════ */}
      <section className="py-24 md:py-32 border-b border-stone-200" data-testid="landing-by-state">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-6">
              <div className="overline mb-6">§ By State</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
                State-specific case files.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-base text-stone-600 font-light leading-relaxed">
                Every state has its own foreclosure procedure — judicial, non-judicial, statutory
                notice windows. Read the Guild playbook for yours.
              </p>
            </div>
          </div>

          <div className="border-t border-l border-stone-200 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {STATE_SLUGS.map((slug, i) => {
              const s = STATES[slug];
              return (
                <Link
                  key={slug}
                  to={`/stop-foreclosure-in/${slug}`}
                  className="border-r border-b border-stone-200 p-6 hover:bg-stone-900 hover:text-stone-50 transition-colors group bg-white"
                  data-testid={`landing-state-${slug}`}
                >
                  <div className="caption text-stone-400 group-hover:text-stone-500 mb-3">
                    {String(i + 1).padStart(2, "0")} · {s.abbrev}
                  </div>
                  <div className="font-serif font-light text-2xl leading-none">{s.name}</div>
                  <div className="mt-4 caption text-stone-500 group-hover:text-stone-300 truncate">
                    {s.saleName}
                  </div>
                  <ArrowUpRight className="h-4 w-4 mt-6 text-stone-400 group-hover:text-stone-50 transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ LATEST CASE FILES · blog highlight ═══════════ */}
      <section className="py-24 md:py-32 bg-stone-50 border-b border-stone-200" data-testid="landing-case-files">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-6">§ Trustee Case Files</div>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
                Read a real case,<br />ledger by ledger.
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/blog" className="btn-ghost" data-testid="landing-blog-all">
                All case files <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 border-t border-l border-stone-200 bg-white">
            {POSTS.slice(0, 3).map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="border-r border-b border-stone-200 p-8 md:p-10 group hover:bg-stone-50 transition-colors"
                data-testid={`landing-case-${p.slug}`}
              >
                <div className="aspect-[16/10] mb-6 overflow-hidden border border-stone-200 bg-stone-100">
                  <img
                    src={p.cover}
                    alt={p.coverAlt}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale contrast-[1.05] group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
                <div className="caption text-stone-400 mb-3">
                  {String(i + 1).padStart(2, "0")} · {new Date(p.date).toLocaleDateString(undefined, { month: "short", year: "numeric" })} · {p.readMinutes} min
                </div>
                <h3 className="font-serif font-light text-2xl leading-tight text-balance group-hover:text-stone-600 transition-colors">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm text-stone-600 font-light leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ MEMBERS MARKETPLACE ═══════════ */}
      <section className="py-24 md:py-40 bg-stone-50 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">§ Members Marketplace</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-balance">
              Homes & land, held privately among members.
            </h2>
            <p className="mt-8 text-lg text-stone-600 font-light leading-relaxed">
              Verified members may list homes for sale, post a home wanted, sell parcels of land,
              or open a land-wanted notice. Discreet, off-market, member-to-member.
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Link to="/listings" className="btn-primary" data-testid="listings-browse-btn">
                Browse listings <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/listings/new" className="btn-outline" data-testid="listings-add-btn">
                List a property
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-2 border-t border-l border-stone-200 bg-white">
            {[
              { k: "home_sale", label: "Homes for sale" },
              { k: "home_wanted", label: "Homes wanted" },
              { k: "land_sale", label: "Land for sale" },
              { k: "land_wanted", label: "Land wanted" },
            ].map((c, i) => (
              <Link
                key={c.k}
                to={`/listings?listing_type=${c.k}`}
                className="p-8 md:p-10 border-r border-b border-stone-200 hover:bg-stone-900 hover:text-white transition-colors group"
                data-testid={`market-tile-${c.k}`}
              >
                <div className="caption text-stone-400 group-hover:text-stone-500 mb-6">
                  0{i + 1}
                </div>
                <h3 className="font-serif font-light text-2xl leading-tight">{c.label}</h3>
                <ArrowUpRight className="h-5 w-5 mt-8 text-stone-400 group-hover:text-white transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ CONTACT ═══════════ */}
      <section id="contact" className="py-24 md:py-40 border-b border-stone-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">§ Correspondence</div>
            <h2
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance"
              data-testid="contact-heading"
            >
              Speak with a trustee.
            </h2>
            <p className="mt-8 text-lg text-stone-600 font-light leading-relaxed">
              A senior Guild trustee will review your situation directly. Sheriff-sale and
              court-deadline cases are flagged urgent and triaged first.
            </p>
            <ul className="mt-10 space-y-4 text-sm text-stone-600">
              {[
                "One-business-day response on routine inquiries.",
                "Same-day response on sheriff-sale alerts.",
                "Encrypted submission direct to the trustee console.",
              ].map((t, i) => (
                <li key={i} className="flex gap-4 items-baseline">
                  <span className="caption text-stone-400">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-light">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-stone-200 bg-white p-8 md:p-12">
              <HomeContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ TRUSTEE ADMIN SIGN-IN STRIP ═══════════ */}
      <section
        className="bg-stone-900 text-stone-100 border-b border-stone-800"
        data-testid="landing-admin-signin"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-stone-400 mb-4">§ Trustee Access</div>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight text-stone-100 text-balance">
              Guild Admin Portal.
            </h2>
            <p className="mt-4 text-stone-400 max-w-2xl">
              Authorized trustees only. Review applications, signatures, uploads, member
              messages, payments, and traffic — all in one console.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/login?redirect=/admin"
              className="inline-flex items-center gap-3 border border-stone-100 text-stone-100 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-stone-100 hover:text-stone-900 transition-colors"
              data-testid="landing-admin-signin-btn"
            >
              <Lock className="h-3.5 w-3.5" />
              Sign in to admin
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
